import { Archive, Language, Logout } from "@mui/icons-material";
import {
  Avatar,
  Box,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "@remix-run/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAvatarUrl } from "~/utils/avatar";
import type { LanguageCode } from "~/utils/languages";
import { useAuth } from "../AuthProvider";
import { ChangeLanguageModal } from "../ChangeLanguageModal";
import { LogoutConfirmModal } from "../LogoutConfirmModal";
import { DeleteAccountFlow } from "../DeleteAccountFlow";

interface UserProfileProps {
  collapsed: boolean;
  onOpenArchivedTeams?: () => void;
  showArchivedTeams?: boolean;
  /**
   * Default true: `margin-top: auto` for main / desktop sidebars that fill the viewport.
   * Use false in compact drawers so the account row sits directly under nav (no empty stretch).
   */
  pinFooterToBottom?: boolean;
  /** Horizontal padding for the row when not collapsed (default 3 theme units). */
  listItemPx?: number;
}

const TruncatableTypography = ({ children, sx, variant }: any) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  const handleMouseEnter = () => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  return (
    <Tooltip title={isTruncated ? children : ""} placement="bottom-start" arrow>
      <Typography
        ref={textRef}
        variant={variant}
        sx={sx}
        onMouseEnter={handleMouseEnter}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};

export const UserProfile: React.FC<UserProfileProps> = ({
  collapsed,
  onOpenArchivedTeams,
  showArchivedTeams,
  pinFooterToBottom = true,
  listItemPx,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { authData } = useAuth();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langDialogOpen, setLangDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [deleteFlowOpen, setDeleteFlowOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!authData) {
      navigate("/login");
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenLanguageModal = () => {
    setLangDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveLanguage = (lang: LanguageCode) => {
    i18n.changeLanguage(lang);
    setLangDialogOpen(false);
  };

  const handleSignOut = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    window.location.href = "/api/auth/sign-out";
  };

  const rowPx = collapsed ? 0 : (listItemPx ?? 3);

  return (
    <Box
      sx={{
        px: 0,
        py: 1.5,
        mt: pinFooterToBottom ? "auto" : 0,
      }}
    >
      <ListItemButton
        sx={{
          borderRadius: 0,
          minHeight: 73,
          justifyContent: collapsed ? "center" : "flex-start",
          px: rowPx,
          py: 1.5,
          "&:hover": {
            backgroundColor: theme.palette.custom.primarySecondaryHover,
          },
        }}
        onClick={handleMenuOpen}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            border: `1px solid ${theme.palette.custom.gray[300]}`,
            bgcolor: theme.palette.custom.gray[200],
            color: theme.palette.custom.gray[600],
            mr: collapsed ? 0 : 1.5,
            fontSize: 13,
          }}
          alt={authData?.name || "User"}
          src={authData ? getAvatarUrl(authData) : ""}
        >
          {authData?.name?.[0]?.toUpperCase()}
        </Avatar>
        {!collapsed && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              mr: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <TruncatableTypography
              variant="h5"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "1rem",
                fontWeight: 500,
                lineHeight: 1.5,
                color: theme.palette.custom.gray[700],
              }}
            >
              {authData?.name}
            </TruncatableTypography>
            <TruncatableTypography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "0.875rem",
                color: theme.palette.custom.gray[600],
                lineHeight: 1.5,
              }}
            >
              {authData?.email}
            </TruncatableTypography>
          </Box>
        )}
      </ListItemButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 201,
              mt: -1,
              ml: 3,
              borderRadius: "var(--Input-Radius-Medium)",
              border: `1px solid ${theme.palette.custom.gray[200]}`,
              boxShadow: "0px 4px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
            },
          },
        }}
      >
        <MenuItem
          onClick={handleOpenLanguageModal}
          sx={{ minHeight: 44, px: 2 }}
        >
          <ListItemIcon>
            <Language
              fontSize="small"
              sx={{ color: theme.palette.custom.gray[700] }}
            />
          </ListItemIcon>
          <Typography variant="body2">{t("auth.changeLanguage")}</Typography>
        </MenuItem>
        {showArchivedTeams && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onOpenArchivedTeams?.();
            }}
            sx={{ minHeight: 44, px: 2 }}
          >
            <ListItemIcon>
              <Archive
                fontSize="small"
                sx={{ color: theme.palette.custom.gray[700] }}
              />
            </ListItemIcon>
            <Typography variant="body2">{t("team.archivedTeams")}</Typography>
          </MenuItem>
        )}
        <MenuItem
          onClick={handleSignOut}
          sx={{ minHeight: 44, px: 2, color: theme.palette.custom.gray[700] }}
        >
          <ListItemIcon>
            <Logout
              fontSize="small"
              sx={{ color: theme.palette.custom.gray[700] }}
            />
          </ListItemIcon>
          <Typography variant="body2">{t("auth.signOut")}</Typography>
        </MenuItem>
      </Menu>

      <ChangeLanguageModal
        open={langDialogOpen}
        onClose={() => setLangDialogOpen(false)}
        currentLanguage={i18n.language.startsWith("ko") ? "ko" : "en"}
        onSave={handleSaveLanguage}
      />

      <LogoutConfirmModal
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        onDeleteAccount={() => {
          setLogoutDialogOpen(false);
          setDeleteFlowOpen(true);
        }}
      />

      <DeleteAccountFlow
        open={deleteFlowOpen}
        email={authData?.email ?? ""}
        onClose={() => setDeleteFlowOpen(false)}
      />
    </Box>
  );
};
